<template>
  <div :class="$style.container">
    <AddressSearch
      :location="searchLocation"
      :allowClear="allowClear"
      :dropdownLayerSize="dropdownLayerSize"
      @onSelectedLocationChange="handleSelectedLocationChange"
      @onClearLocation="onResetClick"
    />
    <HgLink
      v-if="!isSmallScreen && !allowClear"
      name="addressLink"
      tag="button"
      :class="$style.resetLink"
      @click="onResetClick"
    >
      {{ t('searchFilters.resetLabel') }}
    </HgLink>
    <component
      :is="isSmallScreen || isPlatformHg() ? HgSelect : HgSegmentedButton"
      v-model="defaultPropertyType"
      :label="isSmallScreen || isPlatformHg() ? t('searchFilters.propertyTypeLabel') : ''"
      :options="options"
      name="propertyTypeSelect"
      :disabled="isLoading"
      @update:modelValue="onPropertyTypeChange"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgLink, HgSegmentedButton, HgSelect, type HgSelectOptions } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AddressSearch from '@/components/search-bar/address-search/AddressSearch.vue';
import useRouterLocationPush from '@/composables/useRouterLocationPush';
import { isPlatformHg } from '@/helpers/platform';
import { PropertyType } from '@/models/iazi/common/enums';
import useMapStore from '@/stores/useMapStore';
import { trackPropertyTypeSwitch } from '@/tracking';
import { EventSource } from '@/types';
import { type MediaContext } from '@/views/composables/useMedia';

interface Props {
  propertyType: PropertyType;
  searchLocation: GeoLocation | null;
  allowClear?: boolean;
  dropdownLayerSize?: 'small' | 'medium';
}
const props = withDefaults(defineProps<Props>(), {
  allowClear: false,
  dropdownLayerSize: 'medium',
});

const { t } = useI18n();
const mapStore = useMapStore();
const { isLoading } = storeToRefs(mapStore);
const { pushLocation } = useRouterLocationPush();
const { isSmallScreen } = inject<MediaContext>('mediaScreen') as MediaContext;
const defaultPropertyType = ref(props.propertyType);

const emit = defineEmits<{
  // TODO find the lint rule to match the Composition API
  /* eslint-disable unused-imports/no-unused-vars */
  (e: 'onPropertyTypeChange', propertyType: PropertyType): void;
  (e: 'onSelectedLocationChange', value: GeoLocation): void;
}>();

const options = computed<Array<HgSelectOptions>>(() => [
  { text: t('common.house'), value: PropertyType.House },
  { text: t('common.apartment'), value: PropertyType.Apartment },
]);

const onPropertyTypeChange = (value: string | number): void => {
  const propertyType = String(value) as PropertyType;
  trackPropertyTypeSwitch(propertyType);
  emit('onPropertyTypeChange', propertyType);
};

const handleSelectedLocationChange = (value: GeoLocation): void => {
  emit('onSelectedLocationChange', value);
};

const onResetClick = async (): Promise<void> => {
  mapStore.setLoading(true);
  await pushLocation(EventSource.Reset);
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.container {
  display: flex;
  flex-direction: column;
  gap: $space-200;

  @include grid-media-query(sm) {
    gap: $space-150;
  }

  > div[class*='HgFormFieldWrapper_noLabel'] {
    padding-top: 0;
  }
}

.resetLink {
  align-self: center;

  @include body-2-semi-bold;
}
</style>
